import {
	DayState,
	dayStateOf,
	shiftDate,
	weekdayIndex,
	type SponsorAvailability,
} from "sponsor/sponsorCalendar";

/**
 * The `/advertising` closing band's availability row (ui#1861). Plain TypeScript — this module imports
 * NO React — so "which of the next two weeks is still for sale" is a tested rule rather than a chain of
 * `map` and `find` inside a band that gets rewritten on the next restyle.
 *
 * It answers the question with the SAME rule the booking calendar answers it with: {@link dayStateOf}
 * decides what a day is, so a pending day is held on this page for exactly the reason it is held on the
 * calendar next door — it is paid for, and review can still let it run. Restating that here as
 * `!booked.includes(date)` would be the second answer that quietly disagrees, and the disagreement
 * would read as a free day this page cannot actually sell.
 */

/** How many days the row shows. Named because the copy and the row have to agree on the number. */
export const AVAILABILITY_OUTLOOK_DAYS = 14;

/**
 * One cell of the row.
 *
 * Carries its date already decomposed, for the reason {@link OutlookNextFree} does: the cell prints a
 * bare day number, so its accessible name has to spell out the whole date — and composing that in the
 * view would mean a second, hand-rolled `Date` parse beside the one this module already did.
 */
export interface OutlookDay {
	/** ISO `YYYY-MM-DD`, so the cell's own key is unambiguous. */
	date: string;
	/** The number the cell prints. */
	dayOfMonth: number;
	/** Monday-first index into `sponsor.calendar.weekdaysLong`. */
	weekday: number;
	/** 1-12, a one-based index into `sponsor.calendar.months`. */
	month: number;
	/** Bookable today: inside the published window, and held by nothing. */
	free: boolean;
}

/**
 * The first day somebody could actually book, decomposed into the PARTS a catalog builds a date from.
 *
 * Numbers rather than a formatted string, for the reason `sponsorCalendar.monthLabelParts` exists:
 * `toLocaleDateString` would emit English weekday and month names on the Romanian page. The view looks
 * both up in `sponsor.calendar`, which already ships them in each locale.
 */
export interface OutlookNextFree {
	date: string;
	/** Today itself is still bookable, which the copy says instead of dating it. */
	today: boolean;
	/** The day after today, which the copy also says instead of dating it. */
	tomorrow: boolean;
	/** Monday-first index into `sponsor.calendar.weekdaysLong`. */
	weekday: number;
	dayOfMonth: number;
	/** 1-12, a one-based index into `sponsor.calendar.months`. */
	month: number;
}

export interface AvailabilityOutlook {
	days: readonly OutlookDay[];
	/** `null` when every one of the fourteen is spoken for — a real state, not a missing read. */
	nextFree: OutlookNextFree | null;
}

/**
 * The next {@link AVAILABILITY_OUTLOOK_DAYS} days from `today`, and the first of them still for sale.
 *
 * @param today ISO `YYYY-MM-DD` in UTC, matching every other date in the booking flow
 * @param availability the published window, or `null` when the read failed or has not returned
 * @returns the row to draw, or `null` when there is no availability to draw one from
 */
export function availabilityOutlook(
	today: string,
	availability: SponsorAvailability | null,
): AvailabilityOutlook | null {
	if (availability === null) {
		return null;
	}
	const days: OutlookDay[] = Array.from({length: AVAILABILITY_OUTLOOK_DAYS}, (_, offset) => {
		const date = shiftDate(today, offset);
		return {
			date,
			dayOfMonth: dayOfMonth(date),
			weekday: weekdayIndex(date),
			month: monthOfYear(date),
			// Available is the ONE state that is for sale. Booked, pending and out-of-window are each
			// unbuyable for their own reason, and this row has room to say only whether it is free.
			free: dayStateOf(date, availability) === DayState.Available,
		};
	});
	const first = days.find(day => day.free);
	return {
		days,
		nextFree: first === undefined ? null : {
			date: first.date,
			today: first.date === today,
			tomorrow: first.date === shiftDate(today, 1),
			weekday: first.weekday,
			dayOfMonth: first.dayOfMonth,
			month: first.month,
		},
	};
}

/* Read off the ISO string rather than through a `Date`, which would reintroduce a timezone. */
function dayOfMonth(date: string): number {
	return Number(date.slice(8, 10));
}

function monthOfYear(date: string): number {
	return Number(date.slice(5, 7));
}

/** Today in UTC, in the ISO shape every date in the booking flow is written in. */
export function todayIso(now: number = Date.now()): string {
	return new Date(now).toISOString().slice(0, 10);
}
